import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';

/**
 * Canonical Firebase initialization for the Messego web app.
 *
 * Config is read from Vite env vars (see `.env`). The same build works across
 * dev/staging/prod by swapping env values. Email/Password sign-in must be
 * enabled in the Firebase console (Auth → Sign-in method).
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
};

/** True when enough config is present to talk to Firebase. */
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

// Reuse the existing app instance during HMR instead of re-initializing.
export const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth: Auth = getAuth(app);

// Callable functions live in the messego Cloud Functions codebase, region
// pinned to match firebase-functions/functions/src/config/runtime.ts's REGION.
export const functions: Functions = getFunctions(app, import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION);

// Point the local Auth/Functions emulators when explicitly enabled (mirrors
// VITE_USE_EMULATOR used elsewhere in the ecosystem). Guarded so production
// never connects.
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  } catch {
    /* already connected during HMR */
  }
  try {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  } catch {
    /* already connected during HMR */
  }
}

/**
 * Where Firebase email action links (verify-email, password-reset) land.
 * This page (`/auth/reset-password`) inspects `mode` + `oobCode` and completes
 * the flow in-app. Add this domain to Firebase console → Auth → Settings →
 * Authorized domains.
 */
export const ACTION_URL = `${window.location.origin}/auth/reset-password`;

/** Convenience: current user's fresh ID token, or null when signed out. */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  return user ? user.getIdToken(forceRefresh) : null;
}
